import React, { useState, useEffect } from 'react';
import {
    getAuth,
    updatePassword,
    EmailAuthProvider,
    reauthenticateWithCredential,
    multiFactor,
    RecaptchaVerifier,
    PhoneAuthProvider,
    sendEmailVerification,
    PhoneMultiFactorGenerator
} from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from '../firebase/config';
import { useTheme } from '../contexts/ThemeContext';
import { showToast } from '../utils/toast'; // Usando o utilitário
import { Modal, Button, Form } from 'react-bootstrap'; // Importando componentes do Bootstrap
import { Palette, KeyRound, UploadCloud, Sun, Moon, Shield, MailCheck, Monitor } from 'lucide-react';
import MfaModal from '../components/MfaModal'; // Reutilizando o modal de MFA do login

// --- Componente de Modal para Pedir Senha ---
// Um modal simples e local para o prompt de senha.
function PasswordPromptModal({ show, onHide, onSubmit, isSubmitting }) {
    const [password, setPassword] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(password);
        setPassword('');
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>Confirme sua Identidade</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    <p>Para sua segurança, por favor, insira sua senha atual para continuar.</p>
                    <Form.Group>
                        <Form.Label>Senha Atual</Form.Label>
                        <Form.Control
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            autoFocus
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={isSubmitting}>Cancelar</Button>
                    <Button variant="primary" type="submit" disabled={isSubmitting}>
                        {isSubmitting ? 'Confirmando...' : 'Confirmar'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}


// --- Componente Principal da Página ---
function SettingsPage({ user, mspData }) {
    const { themePreference, setTheme } = useTheme();

    // Estados do formulário de senha
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    // Estados de aparência
    const [brandColorLight, setBrandColorLight] = useState(mspData.brandColorLight || '#0d6efd');
    const [brandColorDark, setBrandColorDark] = useState(mspData.brandColorDark || '#5865F2');
    const [isUploadingLight, setIsUploadingLight] = useState(false);
    const [isUploadingDark, setIsUploadingDark] = useState(false);
    
    // Estados do fluxo de MFA
    const [mfaFactors, setMfaFactors] = useState([]);
    const [isEnrollingMfa, setIsEnrollingMfa] = useState(false); // Controla a exibição do form de telefone
    const [phoneNumber, setPhoneNumber] = useState('');
    const [isProcessingMfa, setIsProcessingMfa] = useState(false); // Loading geral para o fluxo
    
    // Estados dos modais do fluxo MFA
    const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
    const [showMfaCodeModal, setShowMfaCodeModal] = useState(false);
    const [mfaVerificationId, setMfaVerificationId] = useState(null);

    useEffect(() => {
        const userMfaFactors = multiFactor(user)?.enrolledFactors || [];
        setMfaFactors(userMfaFactors);
    }, [user]);
    
    useEffect(() => {
        const auth = getAuth();
        if (!window.recaptchaVerifier) {
            window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', { 'size': 'invisible' });
        }
    }, []);

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        if (newPassword !== confirmPassword) {
            showToast('error', 'As novas senhas não coincidem.');
            return;
        }
        if (!currentPassword) {
            showToast('error', 'Por favor, insira a sua senha atual.');
            return;
        }

        try {
            const credential = EmailAuthProvider.credential(user.email, currentPassword);
            await reauthenticateWithCredential(user, credential);
            await updatePassword(user, newPassword);
            showToast('success', 'Senha alterada com sucesso!');
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (error) {
            console.error("Erro ao alterar senha:", error);
            showToast('error', 'Falha ao alterar a senha. Verifique a sua senha atual.');
        }
    };

    const handleBrandLogoUpload = async (e, themeType) => {
        const file = e.target.files[0];
        if (!file) return;

        if (themeType === 'light') setIsUploadingLight(true);
        if (themeType === 'dark') setIsUploadingDark(true);

        const storageRef = ref(storage, `brand_logos/${user.uid}/logo_${themeType}`);
        
        try {
            showToast('info', `Carregando o logo (${themeType})...`);
            const snapshot = await uploadBytes(storageRef, file);
            const logoURL = await getDownloadURL(snapshot.ref);
            
            const userDocRef = doc(db, 'msp_users', user.uid);
            await updateDoc(userDocRef, {
                [themeType === 'light' ? 'brandLogoLightURL' : 'brandLogoDarkURL']: logoURL
            });
            showToast('success', `Logo (${themeType}) atualizado!`);
        } catch (error) {
            console.error("Erro ao carregar o logo:", error);
            showToast('error', 'Falha ao carregar o logo.');
        } finally {
            if (themeType === 'light') setIsUploadingLight(false);
            if (themeType === 'dark') setIsUploadingDark(false);
        }
    };

    const handleColorChange = async (e, themeType) => {
        const newColor = e.target.value;
        if (themeType === 'light') setBrandColorLight(newColor);
        if (themeType === 'dark') setBrandColorDark(newColor);
        
        const userDocRef = doc(db, 'msp_users', user.uid);
        await updateDoc(userDocRef, {
            [themeType === 'light' ? 'brandColorLight' : 'brandColorDark']: newColor
        });
    };
    
    // --- LÓGICA DE MFA REFEITA ---

    // Etapa 1: Usuário clica em "Verificar" -> Abre o modal de senha
    const startMfaEnrollment = (e) => {
        e.preventDefault();
        setShowPasswordPrompt(true);
    };

    // Etapa 2: Usuário submete a senha -> Reautentica e envia o SMS
    const handleReauthAndSendSms = async (password) => {
        if (!password) {
            showToast('warning', 'A senha é obrigatória.');
            return;
        }
        setIsProcessingMfa(true);
        setShowPasswordPrompt(false);

        try {
            // Reautenticação
            const credential = EmailAuthProvider.credential(user.email, password);
            await reauthenticateWithCredential(user, credential);

            // Envio do SMS
            const auth = getAuth();
            const recaptchaVerifier = window.recaptchaVerifier;
            const session = await multiFactor(user).getSession();
            const phoneInfoOptions = { phoneNumber, session };
            const phoneAuthProvider = new PhoneAuthProvider(auth);
            const verificationId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier);
            
            // Sucesso -> Armazena o ID e abre o modal de código
            setMfaVerificationId(verificationId);
            setShowMfaCodeModal(true);

        } catch (error) {
            console.error("Erro no fluxo de MFA:", error);
            showToast('error', 'Falha na autenticação ou envio do SMS. Verifique sua senha e tente novamente.');
        } finally {
            setIsProcessingMfa(false);
        }
    };

    // Etapa 3: Usuário submete o código -> Conclui o registro
    const handleVerifyCodeAndEnroll = async (verificationCode) => {
        setIsProcessingMfa(true);
        setShowMfaCodeModal(false);

        try {
            const cred = PhoneAuthProvider.credential(mfaVerificationId, verificationCode);
            const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
            await multiFactor(user).enroll(multiFactorAssertion, `Celular (${phoneNumber.slice(-4)})`);

            showToast('success', 'Verificação em duas etapas ativada!');
            const userMfaFactors = multiFactor(user)?.enrolledFactors || [];
            setMfaFactors(userMfaFactors);
            setIsEnrollingMfa(false); // Esconde o formulário
            setPhoneNumber('');

        } catch (error) {
            console.error("Erro ao verificar código:", error);
            showToast('error', 'Código inválido ou expirado. Tente novamente.');
        } finally {
            setIsProcessingMfa(false);
        }
    };
    
    const handleSendVerificationEmail = async () => {
        try {
            await sendEmailVerification(user);
            showToast('success', 'Link de verificação enviado para seu e-mail!');
        } catch (error) {
            console.error("Erro ao enviar e-mail de verificação:", error);
            showToast('error', 'Não foi possível enviar o e-mail de verificação.');
        }
    };

    return (
        <>
            <div id="recaptcha-container"></div>
            <h1 className="h3 mb-3">Configurações</h1>

            {!user.emailVerified && (
                <div className="alert alert-warning d-flex align-items-center mb-4" role="alert">
                    <MailCheck className="me-3 flex-shrink-0" />
                    <div>
                        <strong>Seu e-mail ainda não foi verificado.</strong> Para habilitar as configurações de segurança, por favor, valide sua conta primeiro.
                        <button className="btn btn-sm btn-primary ms-3" onClick={handleSendVerificationEmail}>
                            Enviar E-mail de Verificação
                        </button>
                    </div>
                </div>
            )}

            <div className="row g-4">
                <div className="col-lg-6">
                    <div className={`card shadow-sm h-100 ${!user.emailVerified ? 'opacity-50' : ''}`}>
                        <div className="card-header"><h5 className="mb-0 d-flex align-items-center"><KeyRound className="me-2"/> Segurança da Conta</h5></div>
                        <div className="card-body position-relative">
                            <fieldset disabled={!user.emailVerified || isProcessingMfa}>
                                <form onSubmit={handlePasswordChange}>
                                    <h6>Alterar Senha</h6>
                                    <div className="mb-3">
                                        <label className="form-label small" htmlFor="currentPassword">Senha Atual</label>
                                        <input type="password" id="currentPassword" className="form-control" autoComplete="current-password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label small" htmlFor="newPassword">Nova Senha</label>
                                        <input type="password" id="newPassword" className="form-control" autoComplete="new-password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label small" htmlFor="confirmPassword">Confirmar Nova Senha</label>
                                        <input type="password" id="confirmPassword" className="form-control" autoComplete="new-password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
                                    </div>
                                    <button type="submit" className="btn btn-primary">Alterar Senha</button>
                                </form>
                                <hr />
                                <div>
                                    <h6 className="d-flex align-items-center"><Shield className="me-2"/> Verificação em Duas Etapas (MFA)</h6>
                                    {mfaFactors.length > 0 ? (
                                        <div className="mt-3">
                                            <p className="text-success mb-1">A verificação por SMS está ATIVA.</p>
                                            <p className="small text-muted">Fator registrado: <strong>{mfaFactors[0].displayName || mfaFactors[0].phoneNumber}</strong></p>
                                        </div>
                                    ) : (
                                        <div className="mt-3">
                                            {isEnrollingMfa ? (
                                                <form onSubmit={startMfaEnrollment}>
                                                    <div className="form-floating mb-3">
                                                        <input type="tel" className="form-control" id="phoneNumber" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} placeholder="+5511987654321" required />
                                                        <label htmlFor="phoneNumber">Número (formato: +5511...)</label>
                                                    </div>
                                                    <div className="d-flex">
                                                        <button type="submit" className="btn btn-success" disabled={isProcessingMfa}>
                                                            {isProcessingMfa ? 'Aguarde...' : 'Verificar'}
                                                        </button>
                                                        <button type="button" className="btn btn-secondary ms-2" onClick={() => setIsEnrollingMfa(false)} disabled={isProcessingMfa}>Cancelar</button>
                                                    </div>
                                                </form>
                                            ) : (
                                                <>
                                                    <p className="text-muted">Adicione uma camada extra de segurança. Você precisará da sua senha e um código enviado por SMS para fazer login.</p>
                                                    <button className="btn btn-primary" onClick={() => setIsEnrollingMfa(true)}>Ativar Verificação por SMS</button>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </fieldset>
                            {!user.emailVerified && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 2, cursor: 'not-allowed' }}></div>}
                        </div>
                    </div>
                </div>
                <div className="col-lg-6">
                    <div className="card shadow-sm h-100">
                        <div className="card-header"><h5 className="mb-0 d-flex align-items-center"><Palette className="me-2"/> Aparência e Marca</h5></div>
                        <div className="card-body">
                            <div className="mb-4">
                                <label className="form-label fw-bold d-flex align-items-center"><Monitor className="me-2"/> Tema de Exibição</label>
                                <div className="btn-group w-100" role="group">
                                    <button type="button" className={`btn ${themePreference === 'light' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setTheme('light')}>
                                        <Sun className="me-2" size={16} /> Claro
                                    </button>
                                    <button type="button" className={`btn ${themePreference === 'dark' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setTheme('dark')}>
                                        <Moon className="me-2" size={16} /> Escuro
                                    </button>
                                    <button type="button" className={`btn ${themePreference === 'system' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setTheme('system')}>
                                        <Monitor className="me-2" size={16} /> Sistema
                                    </button>
                                </div>
                            </div>
                            <hr />
                            <div className="p-3 mb-3 border bg-light text-dark">
                                <label className="form-label fw-bold d-flex align-items-center"><Sun className="me-2"/> Tema Claro</label>
                                <div className="mb-3">
                                    <label htmlFor="brandLogoLight" className="form-label small"><UploadCloud className="me-1" size={14}/> Logo (Tema Claro)</label>
                                    <div className="d-flex align-items-center">
                                        <img src={mspData.brandLogoLightURL || '/logo_light.png'} alt="Logo da Marca (Claro)" className="me-3 border p-1 bg-white" style={{maxHeight: '32px', maxWidth: '120px'}}/>
                                        <input type="file" id="brandLogoLight" className="form-control form-control-sm" onChange={(e) => handleBrandLogoUpload(e, 'light')} accept="image/*" disabled={isUploadingLight} />
                                    </div>
                                </div>
                                <div>
                                    <label htmlFor="brandColorLight" className="form-label small">Cor Principal (Tema Claro)</label>
                                    <div className="d-flex align-items-center">
                                        <input type="color" id="brandColorLight" className="form-control form-control-color" value={brandColorLight} onChange={(e) => handleColorChange(e, 'light')} />
                                        <span className="ms-2">{brandColorLight}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="p-3 border bg-dark text-white">
                                <label className="form-label fw-bold d-flex align-items-center"><Moon className="me-2"/> Tema Escuro</label>
                                <div className="mb-3">
                                    <label htmlFor="brandLogoDark" className="form-label small"><UploadCloud className="me-1" size={14}/> Logo (Tema Escuro)</label>
                                    <div className="d-flex align-items-center">
                                        <img src={mspData.brandLogoDarkURL || '/logo_dark.png'} alt="Logo da Marca (Escuro)" className="me-3 border border-secondary p-1" style={{maxHeight: '32px', maxWidth: '120px'}}/>
                                        <input type="file" id="brandLogoDark" className="form-control form-control-sm" onChange={(e) => handleBrandLogoUpload(e, 'dark')} accept="image/*" disabled={isUploadingDark} />
                                    </div>
                                </div>
                                <div>
                                    <label htmlFor="brandColorDark" className="form-label small">Cor Principal (Tema Escuro)</label>
                                    <div className="d-flex align-items-center">
                                        <input type="color" id="brandColorDark" className="form-control form-control-color" value={brandColorDark} onChange={(e) => handleColorChange(e, 'dark')} />
                                        <span className="ms-2">{brandColorDark}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modais para o fluxo de MFA */}
            <PasswordPromptModal
                show={showPasswordPrompt}
                onHide={() => setShowPasswordPrompt(false)}
                onSubmit={handleReauthAndSendSms}
                isSubmitting={isProcessingMfa}
            />

            <MfaModal
                show={showMfaCodeModal}
                onHide={() => setShowMfaCodeModal(false)}
                onSubmit={handleVerifyCodeAndEnroll}
                phoneNumberHint={phoneNumber.slice(-4)}
                isVerifying={isProcessingMfa}
            />
        </>
    );
}

export default SettingsPage;